import { ReactNode, useState } from 'react';
import { t } from '@lingui/macro';
import { Drawer, Input, useInputProps } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { observer } from 'mobx-react-lite';

import { SelectSearchList } from '../../../components/ui/ui-fullscreen-select/select-search-list';
import { getNameLocal } from '../../../lib/local/get-title';
import { useLocal } from '../../../lib/local/use-local';
import { SelectStationsInput } from './select-stations-input';

type SelectValue = {
  value: string;
  label: {
    nameRu: string | null;
    nameEn: string | null;
    nameHy: string | null;
  };
};

const closeButtonProps = { size: 'lg' };

type UiFullpageSelectProps = {
  value?: {
    start: SelectValue;
    end: SelectValue;
  };
  label?: ReactNode;
  placeholder?: { start: ReactNode; end: ReactNode };
  listLoading?: boolean;
  stationsStart?: SelectValue[];
  stationsEnd?: SelectValue[];
  onChange?: (values: { start?: SelectValue; end?: SelectValue }) => void;
};

export const SelectStations = observer(
  ({
    value,
    placeholder,
    label,
    stationsStart = [],
    stationsEnd = [],
    listLoading,
    onChange,
    ...props
  }: UiFullpageSelectProps) => {
    const [lang] = useLocal();
    const { wrapperProps } = useInputProps(
      'TextInput',
      {},
      {
        value,
        placeholder,
        label,
        stationsStart,
        stationsEnd,
        listLoading,
        onChange,
        ...props,
      }
    );
    const [opened, { open, close }] = useDisclosure(false);

    const [currentDrawer, setCurrentDrawer] = useState<{
      type: 'start' | 'end';
      title: ReactNode;
      stations: SelectValue[];
    }>({ type: 'start', title: '', stations: [] });

    const handleSelect = (
      item: { value: string; label: SelectValue['label'] } | null
    ) => {
      const changedValue = { ...value, [currentDrawer.type]: item };

      onChange && onChange(changedValue);
      close();
    };

    const handleStartClick = () => {
      setCurrentDrawer({
        type: 'start',
        title: placeholder?.start || '',
        stations: stationsStart,
      });

      open();
    };

    const handleEndClick = () => {
      setCurrentDrawer({
        type: 'end',
        title: placeholder?.end || '',
        stations: stationsEnd,
      });

      open();
    };

    const handleSwitch = () => {
      onChange && onChange({ start: value?.end, end: value?.start });
    };

    return (
      <Input.Wrapper {...wrapperProps} size="md">
        <Drawer
          size="100%"
          position="bottom"
          opened={opened}
          title={currentDrawer.title}
          closeButtonProps={closeButtonProps}
          onClose={close}
        >
          <SelectSearchList
            data={currentDrawer.stations}
            placeholder={t`Введите название станции`}
            onSelect={handleSelect}
            loading={listLoading}
          />
        </Drawer>

        <SelectStationsInput
          placeholder={placeholder}
          value={{
            start: getNameLocal(lang, value?.start.label),
            end: getNameLocal(lang, value?.end.label),
          }}
          onClickStart={handleStartClick}
          onClickEnd={handleEndClick}
          onSwitch={handleSwitch}
        />
      </Input.Wrapper>
    );
  }
);
